import type {
  EpisodeProposal,
  EpisodeTarget,
  LinkedAction,
  LinkedOutreach,
} from '@troyai/portal/intervention-episodes/data-access';
import { EPISODE_AGENT_IDENTITY } from '@troyai/portal/intervention-episodes/data-access';
import {
  DEMO_ACTORS,
  PHARMACY_EPISODE_IDS,
  SCRIPTED_ID_BASE,
  type DemoActor,
  demoMemberId,
  demoPersona,
  storyDate,
  storyIso,
} from '@troyai/portal/investor-demo/core';

import type { DemoEpisode } from '../../demo-data';
import { buildActivity } from '../episode-builders';
import { withAssignments } from '../episodes.fixture';
import { ACTION_IDS, PROPOSAL_ID_BASE, TARGET_ID_BASE, outreachId, recommendationId } from '../ids';

/**
 * The pharmacy story's four episodes, as written for Tue 2026-10-13 09:30 (the dates here and
 * below are the scripted ones; `storyIso` moves each onto the real story day). Episode ids are
 * `SCRIPTED_ID_BASE +` the number shown (`PHARMACY_EPISODE_IDS` in demo-core), all owned by "you",
 * the pharmacist (`demoPersona()`: the signed-in account, Lisa Reyes until it is known):
 * - 9001 Heart failure and kidney safety, stop meloxicam: active since Oct 6, escalated at 09:14
 * - 9002 Statin adherence, renew atorvastatin: active since Sep 28, the renewal came through Oct 12
 * - 9003 Medication affordability, Jardiance copay: active since Oct 5, escalated at 08:50
 * - 9004 Fall immunizations: a draft the agent created at 08:45
 * Three activity rows (Oct 14 and 15, by the pharmacist) stay hidden until the "+3 days" jump.
 *
 * Every target of an active episode has an assignee: what the agent checks from claims or by its
 * calls is Troy Agent's, the counter work is "you" (read in the seed, like the owner), and Extra
 * Help screening is the plan coordinator Dana's. The draft's targets stay unassigned.
 */

const AGENT = DEMO_ACTORS.agent.email;
const DANA = DEMO_ACTORS.dana.email;

/** The pharmacist in prose: the name without credentials ("Lisa Reyes, PharmD" reads "Lisa Reyes"). */
const plainName = (actor: DemoActor): string => actor.name.split(',')[0];

/** Proposals adopt a recommendation; the adoption ids are only ever read as opaque numbers. */
const ADOPTION_ID_BASE = SCRIPTED_ID_BASE + 9450;

/** Fields every demo episode shares: first attempt, nothing earlier. Fresh on every call. */
function firstAttempt(): Pick<
  DemoEpisode,
  'tenant_id' | 'member_global_id' | 'attempt_number' | 'attempt_count' | 'attempt_history'
> {
  return {
    tenant_id: 1,
    member_global_id: demoMemberId(),
    attempt_number: 1,
    attempt_count: 1,
    attempt_history: [],
  };
}

function proposal(
  n: number,
  recommendationRow: number,
  templateName: string,
  createdAt: string
): EpisodeProposal {
  return {
    proposal_id: PROPOSAL_ID_BASE + n,
    recommendation_id: recommendationId(recommendationRow),
    recommendation_version: 1,
    adoption_id: ADOPTION_ID_BASE + n,
    status: 'planned',
    created_at: createdAt,
    source_recommendation_rejected: false,
    template_name: templateName,
  };
}

/**
 * A target without observations. `metricKey` is the bold line on the card (the page words it with
 * `sentenceCase`), `description` the spec's target title. `dueDay` is `yyyy-MM-dd`; the backend
 * sends midnight UTC and the page reads only the day. `assignee` is an email, the agent's identity
 * or `''` (unassigned).
 */
function target(
  n: number,
  metricKey: string,
  description: string,
  required: boolean,
  dueDay: string | null,
  createdAt: string,
  assignee = ''
): EpisodeTarget {
  return {
    target_id: TARGET_ID_BASE + n,
    metric_key: metricKey,
    description,
    baseline: {},
    required,
    ...(dueDay ? { due_date: `${dueDay}T00:00:00Z` } : {}),
    evaluation_contract: null,
    created_at: createdAt,
    observations: [],
    assignee_identity: assignee,
  };
}

/** A call the agent made or answered, linked to an episode. */
function call(
  n: number,
  direction: 'inbound' | 'outbound',
  purpose: string,
  at: string
): LinkedOutreach {
  return {
    member_outreach_id: outreachId(n),
    outreach_channel: 'voice-call',
    direction,
    outcome: 'completed',
    purposes: [purpose],
    created_by: AGENT,
    created_at: at,
    directly_linked: true,
  };
}

function safetyEpisode(you: DemoActor): DemoEpisode {
  const createdAt = storyIso('2026-10-06T16:05:00');
  const fax: LinkedAction = {
    action_global_id: ACTION_IDS.faxToPatel,
    action_template_id: SCRIPTED_ID_BASE + 9650,
    title:
      'Fax to Dr. Patel (orthopedics): discontinuation request + topical diclofenac alternative',
    status: 'completed',
    due_date: storyIso('2026-10-07'),
    created_at: storyIso('2026-10-07T14:40:00'),
    linked_by: AGENT,
    linked_at: storyIso('2026-10-07T14:40:00'),
  };
  return {
    ...firstAttempt(),
    intervention_episode_id: PHARMACY_EPISODE_IDS.safety,
    status: 'active',
    objective: 'Heart failure and kidney safety: stop meloxicam, switch to topical diclofenac',
    owner_identity: you.email,
    version: 2,
    created_by: you.email,
    activated_by: you.email,
    created_at: createdAt,
    updated_at: storyIso('2026-10-13T09:14:00'),
    activated_at: createdAt,
    proposals: [
      proposal(
        1,
        1,
        'Stop meloxicam (NSAID with CHF and CKD 3a), start topical diclofenac',
        createdAt
      ),
    ],
    targets: [
      target(
        1,
        'medication_discontinuation',
        'Meloxicam discontinued',
        true,
        storyIso('2026-10-20'),
        createdAt,
        you.email
      ),
      target(
        2,
        'medication_start',
        'Topical diclofenac started',
        true,
        storyIso('2026-10-20'),
        createdAt,
        you.email
      ),
      target(
        3,
        'daily_weight_logging',
        'Daily weight logged ≥5 days/week',
        false,
        storyIso('2026-11-06'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
      target(
        4,
        'nsaid_free_period',
        'No NSAID fills or OTC purchases for 30 days',
        false,
        storyIso('2026-11-13'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
    ],
    activity: {
      actions: [fax],
      outreach_attempts: [
        call(1, 'outbound', 'medication_education', storyIso('2026-10-07T11:20:00')),
        call(2, 'inbound', 'medication_question', storyIso('2026-10-11T19:42:00')),
      ],
    },
    ...buildActivity(PHARMACY_EPISODE_IDS.safety, [
      {
        kind: 'episode',
        type: 'activated',
        actor: you.email,
        at: createdAt,
        title: 'Recommendation accepted; episode activated',
        note: 'Troy Agent flagged meloxicam with furosemide, lisinopril, CHF and CKD 3a in the fill history.',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-07T11:20:00'),
        title: 'Education call, 9 min',
        note: 'Why NSAIDs strain the heart and kidneys; how to weigh daily; confirmed she has a scale.',
      },
      {
        kind: 'episode',
        type: 'action_linked',
        actor: AGENT,
        at: storyIso('2026-10-07T14:40:00'),
        title: 'Fax to Dr. Patel (orthopedics)',
        note: 'Asked to stop meloxicam; suggested topical diclofenac; pharmacist note attached.',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: AGENT,
        at: storyIso('2026-10-09T12:00:00'),
        title: 'Follow-up fax to Dr. Patel',
        note: 'No reply after 2 business days; resent with a callback number.',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-11T19:42:00'),
        title: 'Inbound call answered after hours, 6 min',
        note: 'She asked whether she can "just stop the pain pill". A clinical question: not answered, flagged for the pharmacist.',
      },
      {
        kind: 'episode',
        type: 'escalated',
        actor: AGENT,
        at: storyIso('2026-10-13T09:14:00'),
        title: `Escalated to ${plainName(you)}`,
        note: 'Dr. Patel has not replied in 4 business days and she is still taking meloxicam. Call notes and talking points are ready.',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: you.email,
        at: storyIso('2026-10-14T10:05:00'),
        title: "Called Dr. Patel's office: agreed to stop meloxicam; diclofenac gel e-prescribed",
        visibleFrom: storyIso('2026-10-16'),
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: you.email,
        at: storyIso('2026-10-15T10:50:00'),
        title: 'Counseled at pickup: stops meloxicam today; showed how to apply the gel',
        visibleFrom: storyIso('2026-10-16'),
      },
    ]),
  };
}

function statinEpisode(you: DemoActor): DemoEpisode {
  // Rows 13 and 14 are not HRA rows: the pharmacy found these two itself, so no suggestion is
  // left "to review" while its episode is already running.
  const createdAt = storyIso('2026-09-28T08:10:00');
  const activatedAt = storyIso('2026-09-28T08:12:00');
  return {
    ...firstAttempt(),
    intervention_episode_id: PHARMACY_EPISODE_IDS.statin,
    status: 'active',
    objective: 'Statin adherence: renew atorvastatin and sync refills',
    owner_identity: you.email,
    version: 2,
    created_by: you.email,
    activated_by: you.email,
    created_at: createdAt,
    updated_at: storyIso('2026-10-12T16:45:00'),
    activated_at: activatedAt,
    proposals: [proposal(2, 13, 'Atorvastatin renewal and 90-day med sync', createdAt)],
    targets: [
      target(
        5,
        'prescription_renewal',
        'Atorvastatin renewal approved',
        true,
        storyIso('2026-10-16'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
      target(
        6,
        'prescription_pickup',
        'Atorvastatin 90-day supply picked up',
        true,
        storyIso('2026-10-16'),
        createdAt,
        you.email
      ),
      target(
        7,
        'statin_pdc',
        'Statin PDC ≥ 80% by Dec 31',
        false,
        storyIso('2026-12-31'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
    ],
    activity: {
      actions: [],
      outreach_attempts: [
        call(3, 'outbound', 'prescriber_request', storyIso('2026-10-02T14:10:00')),
        call(4, 'outbound', 'pickup_reminder', storyIso('2026-10-12T16:45:00')),
      ],
    },
    ...buildActivity(PHARMACY_EPISODE_IDS.statin, [
      { kind: 'recommendation', type: 'generated', at: storyIso('2026-09-26T09:00:00') },
      { kind: 'recommendation', type: 'accepted', actor: you.email, at: createdAt },
      {
        kind: 'episode',
        type: 'activated',
        actor: you.email,
        at: activatedAt,
        title: 'Episode activated',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: AGENT,
        at: storyIso('2026-09-28T08:20:00'),
        title: 'Renewal request sent to Dr. Cole (e-prescribing)',
        note: `Atorvastatin 20 mg ran out ${storyDate('2026-09-25', 'MMM d')} with no refills left.`,
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-02T14:10:00'),
        title: "Call to Dr. Cole's office, 4 min",
        note: 'Request not reviewed yet; the nurse will route it today.',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: AGENT,
        at: storyIso('2026-10-06T09:30:00'),
        title: 'Second renewal request sent',
        note: 'Marked urgent: 11 days without a statin.',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: AGENT,
        at: storyIso('2026-10-12T16:40:00'),
        title: 'Renewal approved: 90-day supply, 3 refills',
        note: 'Filled and bagged with her October synced refills.',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-12T16:45:00'),
        title: 'Pickup call, 3 min',
        note: `Booked ${storyDate('2026-10-15', 'EEEE')} 10:30 and a ride through her plan's ride benefit.`,
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: you.email,
        at: storyIso('2026-10-15T10:40:00'),
        title: 'Picked up: atorvastatin 90-day supply and 3 synced refills',
        visibleFrom: storyIso('2026-10-16'),
      },
    ]),
  };
}

function affordabilityEpisode(you: DemoActor): DemoEpisode {
  const createdAt = storyIso('2026-10-05T10:30:00');
  const activatedAt = storyIso('2026-10-05T10:31:00');
  return {
    ...firstAttempt(),
    intervention_episode_id: PHARMACY_EPISODE_IDS.affordability,
    status: 'active',
    objective: 'Medication affordability: Jardiance (empagliflozin) copay',
    owner_identity: you.email,
    version: 2,
    created_by: you.email,
    activated_by: you.email,
    created_at: createdAt,
    updated_at: storyIso('2026-10-13T08:50:00'),
    activated_at: activatedAt,
    proposals: [
      proposal(3, 14, 'Copay help: insurance check and manufacturer assistance', createdAt),
    ],
    targets: [
      target(
        8,
        'assistance_application',
        'Patient assistance application submitted',
        true,
        storyIso('2026-10-23'),
        createdAt,
        you.email
      ),
      target(
        9,
        'no_missed_fill',
        'No missed empagliflozin fill',
        true,
        storyIso('2026-11-11'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
      target(
        10,
        'extra_help_screen',
        'Extra Help eligibility checked',
        false,
        storyIso('2026-10-30'),
        createdAt,
        DANA
      ),
    ],
    activity: {
      actions: [],
      outreach_attempts: [
        call(5, 'inbound', 'insurance_question', storyIso('2026-10-05T10:12:00')),
        call(6, 'outbound', 'insurance_question', storyIso('2026-10-06T13:00:00')),
        call(7, 'outbound', 'patient_assistance', storyIso('2026-10-09T11:05:00')),
      ],
    },
    ...buildActivity(PHARMACY_EPISODE_IDS.affordability, [
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-05T10:12:00'),
        title: 'Inbound call answered, 7 min',
        note: 'Asked why her Jardiance copay went up to $47. Explained her coverage phase and offered to look for help.',
      },
      { kind: 'recommendation', type: 'accepted', actor: you.email, at: createdAt },
      {
        kind: 'episode',
        type: 'activated',
        actor: you.email,
        at: activatedAt,
        title: 'Episode activated',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-06T13:00:00'),
        title: "Call to the plan's member services, 22 min (17 on hold)",
        note: `Confirmed the tier change on ${storyDate('2026-10-01', 'MMM d')}; she can apply for Extra Help.`,
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-09T11:05:00'),
        title: "Call to the manufacturer's assistance line, 52 min (38 on hold)",
        note: 'She can apply; the application packet came by fax.',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: AGENT,
        at: storyIso('2026-10-12T15:20:00'),
        title: 'Application pre-filled',
        note: `Income and household from the ${storyDate('2026-10-05', 'MMM d')} call; prescriber section faxed to Dr. Cole.`,
      },
      {
        kind: 'episode',
        type: 'escalated',
        actor: AGENT,
        at: storyIso('2026-10-13T08:50:00'),
        title: `Escalated to ${plainName(you)}`,
        note: "Needs a pharmacist's review and signature before it goes out.",
      },
    ]),
  };
}

function immunizationsDraft(you: DemoActor): DemoEpisode {
  const createdAt = storyIso('2026-10-13T08:45:00');
  return {
    ...firstAttempt(),
    intervention_episode_id: PHARMACY_EPISODE_IDS.immunizations,
    status: 'draft',
    objective: 'Fall immunizations: high-dose flu and pneumococcal (PCV20)',
    owner_identity: you.email,
    version: 1,
    created_by: AGENT,
    created_at: createdAt,
    updated_at: createdAt,
    proposals: [proposal(4, 4, 'Fall immunizations (high-dose flu, PCV20)', createdAt)],
    targets: [
      target(
        11,
        'influenza_vaccination',
        'High-dose flu vaccine given',
        true,
        storyIso('2026-10-31'),
        createdAt
      ),
      target(
        12,
        'pneumococcal_vaccination',
        'Pneumococcal vaccine (PCV20) given',
        true,
        storyIso('2026-10-31'),
        createdAt
      ),
      target(
        13,
        'registry_report',
        'Reported to the state immunization registry',
        false,
        null,
        createdAt
      ),
    ],
    activity: { actions: [], outreach_attempts: [] },
    ...buildActivity(PHARMACY_EPISODE_IDS.immunizations, [
      { kind: 'recommendation', type: 'generated', at: storyIso('2026-10-12T10:30:00') },
      {
        kind: 'recommendation',
        type: 'accepted',
        actor: you.email,
        at: storyIso('2026-10-13T08:42:00'),
      },
      {
        kind: 'episode',
        type: 'created',
        actor: AGENT,
        at: createdAt,
        title: 'Episode drafted',
        note: `Screened from claims and the state registry: no PCV on record, flu due this season. Both can be given at ${storyDate('2026-10-15', 'EEEE')}'s pickup; consent forms ready.`,
      },
    ]),
  };
}

/** The four episodes: safety, statin and affordability active, the immunizations a draft. */
export function seedPharmacyEpisodes(): DemoEpisode[] {
  // Read per call, not per module: the signed-in account arrives at runtime.
  const you = demoPersona();
  return [
    withAssignments(safetyEpisode(you)),
    withAssignments(statinEpisode(you)),
    withAssignments(affordabilityEpisode(you)),
    immunizationsDraft(you),
  ];
}
